import React, { useState, useEffect } from "react";
import { Edit3, PencilLine, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import ApiService from "../../services/ApiService";
// import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
function AboutSection() {
  const [loading, setLoading] = useState(true); //should be true
  const [editMode, setEditMode] = useState(false);
  const [aboutData, setAboutData] = useState({
    organization_name: "",
    organization_email: "",
    organization_domain: "",
    organization_context: "",
    custom_prompt: "",
  });

  const [formData, setFormData] = useState({ ...aboutData });
  const [updating, setUpdating] = useState(false);

  const loadContent = async () => {
    setLoading(true);

    try {
      const data = await ApiService.getOrganizationData();

      const cleanData = {
        organization_name: data.organization_name || "",
        organization_email: data.organization_email || "",
        organization_domain: data.organization_domain || "",
        organization_context: data.organization_context || "",
        custom_prompt: data.custom_prompt || "",
      };

      setAboutData(cleanData);
      setFormData(cleanData);
    } catch (error) {
      toast.error("Failed to Organization load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setUpdating(true);

    try {
      //update endpoint
      await ApiService.updateOrgFields(formData);

      toast.success("Changes saved!");

      setAboutData(formData); //update the changes in UI
      setEditMode(false);
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setFormData(aboutData);
    setEditMode(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            About Your Organization
          </h2>
          <p className="text-gray-600 mt-2">
            Customize the way your Agents interact
          </p>
        </div>

        {!editMode && (
          <button
            onClick={() => setEditMode(true)}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:-translate-y-0.5"
          >
            <Edit3 className="h-5 w-5 mr-2" /> Edit
          </button>
        )}
      </div>

      {/* Organization Details */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <h3 className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Organization Details
          </h3>
        </div>
        <div className="p-6 space-y-6">
          {/* Organization Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Name
            </label>
            {editMode ? (
              <input
                type="text"
                name="organization_name"
                value={formData.organization_name}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                placeholder="Enter organization name"
              />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                <p className="text-gray-800">
                  {aboutData.organization_name || "Not specified"}
                </p>
              </div>
            )}
          </div>

          {/* Support Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Support Email
            </label>
            {editMode ? (
              <input
                type="email"
                name="organization_email"
                value={formData.organization_email}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                placeholder="Enter support email"
              />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                <p className="text-gray-800">
                  {aboutData.organization_email || "Not specified"}
                </p>
              </div>
            )}
          </div>

          {/* Domain */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Domain
            </label>
            {editMode ? (
              <input
                type="text"
                name="organization_domain"
                value={formData.organization_domain}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                placeholder="Enter domain URL"
              />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                <p className="text-gray-800">
                  {aboutData.organization_domain || "Not specified"}
                </p>
              </div>
            )}
          </div>

          {/* Custom Prompt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Prompt
            </label>
            {editMode ? (
              <textarea
                name="custom_prompt"
                value={formData.custom_prompt}
                onChange={handleFormChange}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none"
                placeholder="Enter custom prompt for AI interactions"
              />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 min-h-[120px]">
                {aboutData.custom_prompt ? (
                  <p className="text-gray-800 whitespace-pre-wrap">
                    {aboutData.custom_prompt}
                  </p>
                ) : (
                  
                    <p className="text-gray-500">
                      Click on "Edit" to add Custom Prompt
                      <br /><br />
                      Examples:
                      <ul>
                        <li>
                          <strong>1. Add brand-specific instructions:</strong> "When talking about our product, emphasize its eco-friendly and sustainable features."
                        </li>
                        <li>
                          <strong>2. Tailor language:</strong> "Respond in the language of the inquiry. If a customer's email is in Spanish, reply in Spanish. If it's in French, reply in French."
                        </li>
                        <li>
                          <strong>3. Define persona and tone:</strong> "Act as a friendly support agent for a fitness brand. Your responses should be positive and encouraging."
                        </li>
                      </ul>

                    </p>                    
                  
                )}
              </div>
            )}
          </div>

          {/* Context for AI */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Context for AI
            </label>
            {editMode ? (
              <textarea
                name="organization_context"
                value={formData.organization_context}
                onChange={handleFormChange}
                rows={12}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none"
                placeholder="Provide context about your organization to help AI understand your domain and requirements"
              />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 max-h-80 overflow-y-auto">
                {aboutData.organization_context ? (
                  <div className="text-gray-800 leading-relaxed prose prose-sm max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // Headings
                        h1: ({ node, ...props }) => (
                          <h1
                            className="text-lg font-bold text-gray-900 mb-3 mt-4 border-b border-gray-200 pb-1"
                            {...props}
                          />
                        ),
                        h2: ({ node, ...props }) => (
                          <h2
                            className="text-base font-semibold text-gray-800 mb-2 mt-3"
                            {...props}
                          />
                        ),
                        h3: ({ node, ...props }) => (
                          <h3
                            className="text-sm font-medium text-gray-800 mb-2 mt-3"
                            {...props}
                          />
                        ),
                        h4: ({ node, ...props }) => (
                          <h4
                            className="text-sm font-medium text-gray-700 mb-1 mt-2"
                            {...props}
                          />
                        ),

                        // Paragraphs
                        p: ({ node, ...props }) => (
                          <p
                            className="text-gray-900 leading-relaxed mb-2 text-sm"
                            {...props}
                          />
                        ),

                        // Lists
                        ul: ({ node, ...props }) => (
                          <ul
                            className="list-disc list-inside space-y-1 mb-3 ml-2"
                            {...props}
                          />
                        ),
                        ol: ({ node, ...props }) => (
                          <ol
                            className="list-decimal list-inside space-y-1 mb-3 ml-2"
                            {...props}
                          />
                        ),
                        li: ({ node, ...props }) => (
                          <li
                            className="text-gray-900 leading-relaxed text-sm"
                            {...props}
                          />
                        ),

                        // Links
                        a: ({ node, ...props }) => (
                          <a
                            className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors text-sm"
                            {...props}
                          />
                        ),

                        // Code
                        code: ({ node, inline, ...props }) =>
                          inline ? (
                            <code
                              className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded font-mono text-xs"
                              {...props}
                            />
                          ) : (
                            <code
                              className="block bg-gray-800 text-gray-100 p-3 rounded-lg font-mono text-xs overflow-x-auto"
                              {...props}
                            />
                          ),

                        pre: ({ node, ...props }) => (
                          <pre
                            className="bg-gray-800 rounded-lg mb-3 overflow-hidden"
                            {...props}
                          />
                        ),

                        // Blockquotes
                        blockquote: ({ node, ...props }) => (
                          <blockquote
                            className="border-l-4 border-blue-300 pl-3 italic text-gray-700 bg-blue-50 py-2 rounded-r-lg mb-3 text-sm"
                            {...props}
                          />
                        ),

                        // Tables
                        table: ({ node, ...props }) => (
                          <div className="overflow-x-auto mb-3">
                            <table
                              className="min-w-full border-collapse border border-gray-300 text-sm"
                              {...props}
                            />
                          </div>
                        ),
                        th: ({ node, ...props }) => (
                          <th
                            className="border border-gray-300 bg-gray-100 px-3 py-1.5 text-left font-semibold text-gray-800 text-xs"
                            {...props}
                          />
                        ),
                        td: ({ node, ...props }) => (
                          <td
                            className="border border-gray-300 px-3 py-1.5 text-gray-900 text-xs"
                            {...props}
                          />
                        ),

                        // Strong and emphasis
                        strong: ({ node, ...props }) => (
                          <strong
                            className="font-semibold text-gray-900"
                            {...props}
                          />
                        ),
                        em: ({ node, ...props }) => (
                          <em className="italic text-gray-800" {...props} />
                        ),

                        // Horizontal rule
                        hr: ({ node, ...props }) => (
                          <hr className="border-gray-300 my-4" {...props} />
                        ),
                      }}
                    >
                      {aboutData.organization_context}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-gray-500">
                    Click "Edit" to add organization context
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Action buttons at bottom when in edit mode */}
          {editMode && (
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200/50 sm:justify-end">
              <button
                onClick={handleCancel}
                className="inline-flex items-center justify-center px-6 py-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-xl shadow-md hover:shadow-lg hover:bg-gray-50 transition-all duration-200 transform hover:-translate-y-0.5"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updating}
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:-translate-y-0.5"
              >
                {updating ? (
                  <Loader2 className="animate-spin w-5 h-5 mr-2" />
                ) : null}
                {updating ? "Saving Changes..." : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AboutSection;
